import { createInertiaApp, type ResolvedComponent } from '@inertiajs/react'
import { StrictMode, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import Layout from '../components/Layout'
import axios from 'axios'

// CSRF: read FillMyCalendar's fresh token cookie on EVERY request rather than
// pinning the <meta> token captured at page load. The cookie must be app-specific:
// browsers share cookies between localhost ports, so another local Rails app that
// writes the conventional XSRF-TOKEN name would otherwise replace this app's token.
// Inertia is a SPA, so the meta tag never refreshes after the initial load while
// ApplicationController#set_csrf_cookie keeps this cookie current on every response.
axios.defaults.xsrfCookieName = 'FILLMYCALENDAR-XSRF-TOKEN';
axios.defaults.xsrfHeaderName = 'X-CSRF-Token';
axios.defaults.withXSRFToken = true;

const defaultLayout = (page: ReactNode) => <Layout>{page}</Layout>

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  // title: title => title ? `${title} - App` : 'App',

  // Disable progress bar
  //
  // see https://inertia-rails.dev/guide/progress-indicators
  // progress: false,

  resolve: (name) => {
    const pages = import.meta.glob<{default: ResolvedComponent}>('../pages/**/*.tsx', {
      eager: true,
    })
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
    }

    // To use a default layout, set it on the resolved page.
    // see https://inertia-rails.dev/guide/pages#default-layouts
    page.default.layout ||= defaultLayout


    return page
  },

  setup({ el, App, props }) {
    createRoot(el).render(
      <StrictMode>
        <App {...props} />
      </StrictMode>
    )
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
    },
    future: {
      useScriptElementForInitialPage: true,
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
      "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
      'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
    )
  }
})
